import { useCallback } from 'react'
import { Form, OverlayTrigger, Tooltip as BootstrapTooltip, Row } from 'react-bootstrap'
import classnames from 'classnames'

import { useAccount } from '../../../common/useAccount'

import { useUpdateAccountMutation } from '../../../store/accountUserSettings/accountUserSettings'

import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { EnterpriseManagedBanner } from '../Settings/EnterpriseManagedBanner'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'

import McpPromoIcon from '../../../assets/images/transcript-mcp-promo-icon.svg?url'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import { MCP_HELP_ARTICLE_URL } from '../../OAuth2App/constants'

export const TranscriptSettings: React.FC = () => {
  const { account, accountId } = useAccount({ refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: isAccountUpdateLoading }] = useUpdateAccountMutation()
  const { trackingClient } = useTracking()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)

  const isDownloadTranscriptEnterpriseManaged = account?.enterpriseConfig?.downloadTranscriptAvailable != null
  const isEnabled = account?.effectiveConfig.downloadTranscriptAvailable ?? false

  const handleToggle = useCallback(async () => {
    if (!account) {
      return
    }
    const nextEnabled = !account.effectiveConfig.downloadTranscriptAvailable
    await updateAccount({
      accountId,
      payload: { config: { ...account.config, downloadTranscriptAvailable: nextEnabled } },
    }).unwrap()
    if (nextEnabled) {
      trackingClient.track({ eventName: TrackEvent.TRANSCRIPT_ACCESS_ENABLED, eventDetails: { accountId } })
    }
  }, [account, accountId, updateAccount, trackingClient])

  const handleMcpPromoClick = useCallback(() => {
    trackingClient.track({ eventName: TrackEvent.TRANSCRIPT_SETTINGS_MCP_PROMO_CLICKED, eventDetails: { accountId, currentUserId } })
  }, [trackingClient, accountId, currentUserId])

  if (!account) {
    return null
  }

  return (
    <div className='d-flex flex-column zocks-gap-2.5'>
      <h4>Transcripts</h4>
      <Row className={classnames('p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded zocks-p-1.25 mx-0')}>
        <div className='d-flex align-items-center gap-3'>
          <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
          <span className='zocks-text-gray-900'>Read any past meeting transcript in full, or download it as a CSV file.</span>
        </div>
      </Row>
      {isDownloadTranscriptEnterpriseManaged ? (
        <EnterpriseManagedBanner className='zocks-p-1.25 mx-0' message="This setting is managed by your enterprise and can't be changed here." />
      ) : null}
      <div className='d-flex gap-3'>
        <ScopedBlock
          scope={{
            scope: 'account:update',
            superScope: ['account:admin'],
          }}
          missingScopeAction={MissingScopeAction.REPLACE}
          replaceElement={
            <OverlayTrigger
              trigger={['hover', 'focus']}
              placement='top'
              overlay={<BootstrapTooltip className='zocks-text-gray-500'>Your account admin can edit this setting.</BootstrapTooltip>}
            >
              <Form.Switch disabled={true} checked={isEnabled} />
            </OverlayTrigger>
          }
        >
          <Form.Switch onChange={handleToggle} disabled={isAccountUpdateLoading || isDownloadTranscriptEnterpriseManaged} checked={isEnabled} />
        </ScopedBlock>
        <div className='d-flex flex-column gap-1'>
          <span className={classnames('paragraph-medium', { 'd-flex align-items-center gap-2': isDownloadTranscriptEnterpriseManaged })}>
            {isDownloadTranscriptEnterpriseManaged ? <i className='bi bi-lock zocks-text-gray-700' /> : null}
            Enable transcript access
          </span>
          <span className='paragraph-small zocks-text-gray-700'>All users in your account will be able to read and download meeting transcripts.</span>
        </div>
      </div>
      <div className='d-flex gap-3 align-items-start zocks-p-1.25 zocks-bg-color-gray-100 border zocks-border-color-gray-300 zocks-border-0.5'>
        <img className='flex-shrink-0' src={McpPromoIcon} alt='' />
        <div className='d-flex flex-column gap-1'>
          <span className='paragraph-medium zocks-text-gray-900'>
            <strong>Skip the download.</strong> Ask questions across all your meetings in your AI tool. Connect the Zocks MCP and get answers instantly. No
            files to download.
          </span>
          <a
            className='paragraph-small zocks-text-primary text-decoration-underline'
            href={MCP_HELP_ARTICLE_URL}
            target='_blank'
            rel='noopener noreferrer'
            onClick={handleMcpPromoClick}
          >
            See how it works
          </a>
        </div>
      </div>
    </div>
  )
}
